import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { useGetListQuery } from "@/pages/Private/redux/list/list.api";

import { LoadingOverlay } from "@/components";

import { ListSourceType } from "@/enum/list.enum";

import { StepInfo } from "@/components/StepInfo/StepInfo";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { List } from "../../schema/list";
import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { countListSources } from "../../utils/countListSources";
import { EditableListSource, useEditSourceParam } from "./useEditSourceParam";
import { AddedSourceList } from "../../components/AddedSourceList";
import { Map } from "../../../Map/schema/map";
import { Job } from "../../../Job/schema/job";
import { CreateCsvEnrichmentForm } from "../../../CsvEnrichment/components/CreateCsvEnrichmentForm";
import { CsvEnrichment } from "../../../CsvEnrichment/schema/csvEnrichment";
import { CreateLiEventForm } from "../../../LiEvent/components/CreateLiEventForm";
import { LiEvent } from "../../../LiEvent/schema/liEvent";
import { CreateLiCompanyActivityForm } from "../../../LiCompanyActivity/components/CreateLiCompanyActivityForm";
import { LiCompanyActivity } from "../../../LiCompanyActivity/schema/liCompanyActivity";
import { CreateLiPostForm } from "../../../LiPost/components/CreateLiPostForm";
import { LiPost } from "../../../LiPost/schema/liPost";
import { Stepper } from "../ListEditDetails/components/Stepper";
import { LiProfileVisits } from "../../../LiProfileVisits/schema/liProfileVisits";
import { LiPeople } from "../../../LiPeople/schema/liPeople";
import { CreateLiPeopleForm } from "../../../LiPeople/components/CreateLiPeopleForm";
import { CustomEvent } from "../../../CustomEvent/schema/customEvent";
import { RestartProcessingModal } from "../../components/DownloadSource/RestartProcessingModal";
import { CustomEventSourceSection } from "./components/CustomEventSourceSection";
import { useSourceStepSubmit } from "./useSourceStepSubmit";
import { useSourceFileDownload } from "./useSourceFileDownload";
import { MapsSource } from "./components/MapsSource";
import { JobsSource } from "./components/JobsSource";
import { LiProfileVisitsSource } from "./components/LiProfileVisits";
import { LiSn } from "../../../LiSn/schema/liSn";
import { CreateLiSnForm } from "../../../LiSn/components/LiSnForm";
import { getListConfigurationStatus } from "../../utils/useListConfigurationStatus";
import { FeaturesCodes } from "../../../Users/enum/codes";

import { FundingMa } from "../../../FundingMa/schema/funding-ma";
import { CreateFundingMaForm } from "../../../FundingMa/components/CreateFundingMa";
import { CreateSwarmForm } from "../../../Swarm/components/CreateSwarm";
import { Swarm } from "../../../Swarm/schema/swarm";
import { CreateCompanyEnrichForm } from "../../../CompanyEnrich/components/CreateCompanyEnrichForm";
import { CompanyEnrich } from "../../../CompanyEnrich/schema/companyEnrich";
import { TrackerSourcesStep } from "../../components/TrackerSources/TrackerSourcesStep";
import { ParallelAiSource } from "./components/ParallelAiSource";
import { ParallelAi } from "../../../ParallelAi/schema/parallelAi";
import { ListEditSmartListSource } from "../ListEditSmartListSource/ListEditSmartListSource";

export const ListEditSource: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();

	const navigate = useNavigate();
	const ts = (key: string) => t(`list.${key}`);

	const { data, isFetching } = useGetListQuery(+id);

	const { submit, isRestartPromptOpen, isRestarting, confirmRestart, skipRestart } =
		useSourceStepSubmit(data as List | undefined);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const { downloadIsLoading, handleDownload } = useSourceFileDownload();

	const [selected, setSelected] = useState<
		| Map
		| Job
		| CsvEnrichment
		| LiEvent
		| LiCompanyActivity
		| LiPost
		| LiProfileVisits
		| LiPeople
		| LiSn
		| ParallelAi
		| CustomEvent
		| FundingMa
		| Swarm
		| CompanyEnrich
		| null
	>(null);

	const [duplicated, setDuplicated] = useState<
		| Map
		| Job
		| CsvEnrichment
		| LiEvent
		| LiCompanyActivity
		| LiPost
		| LiProfileVisits
		| LiPeople
		| LiSn
		| ParallelAi
		| FundingMa
		| Swarm
		| CompanyEnrich
		| null
	>(null);

	const amountOfSources = useMemo(() => countListSources(data), [data]);

	// Deep link from the Sources header dropdown: ?editSource=<id> opens that source for editing.
	const handleDeepLinkSelect = useCallback((source: EditableListSource) => {
		setSelected(source);
		setDuplicated(null);
	}, []);

	useEditSourceParam(data, handleDeepLinkSelect);

	if (data?.sourceType === ListSourceType.SMART_LIST) {
		return isFetching || !data ? (
			<LoadingOverlay />
		) : (
			<ListEditSmartListSource data={data as List} />
		);
	}

	return isFetching || !data ? (
		<LoadingOverlay />
	) : (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="source"
				doneSteps={getDoneSteps(data)}
				listId={data.id}
			/>
			<div className="w-full">
				<MapsSource
					data={data}
					duplicated={duplicated as Map}
					selected={selected as Map}
					setDuplicated={setDuplicated}
					setSelected={setSelected}
				/>
				<JobsSource
					data={data}
					duplicated={duplicated as Job}
					selected={selected as Job}
					setDuplicated={setDuplicated}
					setSelected={setSelected}
				/>

				<ParallelAiSource
					data={data}
					duplicated={duplicated}
					selected={selected}
					setDuplicated={setDuplicated}
					setSelected={setSelected}
				/>

				{data.specialType === null && data.sourceType === ListSourceType.CSV_ENRICHMENTS && (
					<>
						{data.entityType === CsvEnrichmentType.ORGANIZATION && (
							<StepInfo
								description={
									<>
										<p>
											Signalist.io lets you upload a company list (with names, sites or LinkedIn
											links), define target roles and locations, apply company-level filters (like
											HQ country or size), choose how many contacts per firm and whether to grab
											emails/phones, then hit activate—its AI instantly finds and compiles the
											matching decision-makers for you.
										</p>
									</>
								}
								title="Create a list of companies from an XLSX upload with Signalist"
								videoId="lxWj4Q2G0iY"
							/>
						)}
						{data.entityType === CsvEnrichmentType.PERSON && (
							<StepInfo
								description={
									<>
										<p>
											You begin by dragging your Excel file into Signalist, which automatically
											detects its columns; you then assign each one to the appropriate contact
											property—such as email address, company name and position—while choosing to
											ignore or save any extra fields as custom data. After clicking the Upload
											button, the file becomes manageable: you can download it again later, remove
											it, or append additional files over time. Finally, you click Continue, give
											your new list a name (for example, “robot network”) and proceed directly into
											setting up your lead qualification criteria and other list parameters.
										</p>
									</>
								}
								title="Create a list of people from an XLSX upload with Signalist"
								videoId="jTYBYk1KSSk"
							/>
						)}
						<div className="flex flex-row gap-12">
							<div className="grow relative">
								{selected ? (
									<CreateCsvEnrichmentForm
										csvEnrichment={selected as CsvEnrichment}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : (
									<CreateCsvEnrichmentForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									downloadIsLoading={downloadIsLoading}
									handleDownload={handleDownload}
									handleSelect={setSelected}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}
				{data.specialType === null && data.sourceType === ListSourceType.JOB_CHANGE && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Upload a list of people you want to monitor for job changes. When someone in
										your list changes jobs, they can automatically be added as active leads based on
										their score. Map the columns from your Excel file to the appropriate fields like
										name, company, and LinkedIn URL.
									</p>
								</>
							}
							title="Upload people to track for job changes"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative">
								{selected ? (
									<CreateCsvEnrichmentForm
										csvEnrichment={selected as CsvEnrichment}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : (
									<CreateCsvEnrichmentForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									downloadIsLoading={downloadIsLoading}
									handleDownload={handleDownload}
									handleSelect={setSelected}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}
				{data.specialType === null && data.sourceType === ListSourceType.TRACKER && (
					<TrackerSourcesStep list={data as List} />
				)}
				<LiProfileVisitsSource
					data={data}
					selected={selected as LiProfileVisits}
					setSelected={setSelected}
				/>
				{data.sourceType === ListSourceType.LI_EVENTS && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist.io lets you automatically compile leads from LinkedIn event attendees
										in three ways: by keyword (e.g. “sales AI”), by a specific event, or by events
										hosted by particular companies (such as McKinsey or Accenture). Enter your
										search terms or event details, set filters—like connection degree and location
										(e.g. Germany)—and optionally qualify leads or limit how many to collect. Once
										configured, Signalist.io runs the scrape and delivers your customized prospect
										list.
									</p>
								</>
							}
							title="Build lead lists based on Linkedin events"
							videoId="mZS_4q6IrGs"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative">
								{selected ? (
									<CreateLiEventForm
										handleSelect={setSelected}
										liEvent={selected as LiEvent}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateLiEventForm
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										liEvent={duplicated as LiEvent}
										list={data as List}
									/>
								) : (
									<CreateLiEventForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}
				{data.sourceType === ListSourceType.LI_SN && (
					<div className="flex flex-row gap-12">
						<div className="grow relative max-w-[50%]">
							{selected ? (
								<CreateLiSnForm
									handleSelect={setSelected}
									liSn={selected as LiSn}
									list={data as List}
								/>
							) : duplicated ? (
								<CreateLiSnForm
									handleDuplicate={setDuplicated}
									handleSelect={setSelected}
									liSn={duplicated as LiSn}
									list={data as List}
								/>
							) : (
								<CreateLiSnForm list={data as List} />
							)}
						</div>
						<div className="w-1/2">
							<AddedSourceList
								handleDuplicate={(item) => {
									setDuplicated(item);
									setSelected(null);
								}}
								handleSelect={(item) => {
									setSelected(item);
									setDuplicated(null);
								}}
								list={data as List}
							/>
						</div>
					</div>
				)}
				{data.sourceType === ListSourceType.LI_COMPANY_ACTIVITY && (
					<>
						<StepInfo
							description={
								<>
									<p>
										In Signalist, you simply name your list, paste your company’s LinkedIn URL, and
										pick—or let the system pick—a profile with admin rights to scrape followers. You
										then choose the time frame (now, historical, or limited to your last X
										followers), save it, set any lead-qualification filters, and decide if it runs
										once or daily with a cap on how many profiles to collect.
									</p>
								</>
							}
							title="How to Track Your LinkedIn Company Followers"
							videoId="jTkrIBkmRNo"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative max-w-[50%]">
								{selected ? (
									<CreateLiCompanyActivityForm
										company={selected as LiCompanyActivity}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateLiCompanyActivityForm
										company={duplicated as LiCompanyActivity}
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : (
									<CreateLiCompanyActivityForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}
				{data.sourceType === ListSourceType.LI_POST && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist.io lets you automatically assemble LinkedIn engagement–based leads in
										three simple ways. You can track all posts about a given keyword or Boolean
										combination (for example “sales AND AI”) over the past 30 days and capture
										authors, likers or commenters (with per-post limits). Alternatively, by pasting
										any LinkedIn post’s URL you can pull in everyone who’s liked or commented on
										that specific update. Finally, you can follow a particular person or company’s
										feed (optionally filtered by keywords) and harvest engagement from all their
										recent posts.
									</p>
								</>
							}
							title="How to build lead lists based on Linkedin engagement"
							videoId="T3opP092v6c"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative w-full max-w-[50%]">
								{selected ? (
									<CreateLiPostForm
										handleSelect={setSelected}
										liPost={selected as LiPost}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateLiPostForm
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										liPost={duplicated as LiPost}
										list={data as List}
									/>
								) : (
									<CreateLiPostForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}

				{data.sourceType === ListSourceType.FUNDING_ROUNDS && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist.io lets you automatically assemble leads from Funding Rounds. You can
										track all Funding Rounds based on country and industry and round type over the
										last X days.
									</p>
								</>
							}
							title="How to build lead lists based on Funding Rounds"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative w-full max-w-[50%]">
								{selected ? (
									<CreateFundingMaForm
										fundingMa={selected as FundingMa}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateFundingMaForm
										fundingMa={duplicated as FundingMa}
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : (
									<CreateFundingMaForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}

				{data.sourceType === ListSourceType.LEAD_DATABASE && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist.io lets you automatically assemble leads from Lead Databases. You can
										track all Lead Databases based on country, industry and job title and other
										filters.
									</p>
								</>
							}
							title="How to build lead lists based on Lead Databases"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative w-full max-w-[50%]">
								{selected ? (
									<CreateSwarmForm
										handleSelect={setSelected}
										list={data as List}
										swarm={selected as Swarm}
									/>
								) : duplicated ? (
									<CreateSwarmForm
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										list={data as List}
										swarm={duplicated as Swarm}
									/>
								) : (
									<CreateSwarmForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}

				{data.sourceType === ListSourceType.COMPANY_ENRICH && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist.io lets you automatically assemble leads from our Lead Database. You
										can search for leads based on position, seniority, department, country, domain
										and other filters.
									</p>
								</>
							}
							title="How to build lead lists based on Lead Databases"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative w-full max-w-[50%]">
								{selected ? (
									<CreateCompanyEnrichForm
										companyEnrich={selected as CompanyEnrich}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateCompanyEnrichForm
										companyEnrich={duplicated as CompanyEnrich}
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										list={data as List}
									/>
								) : (
									<CreateCompanyEnrichForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}

				{data.sourceType === ListSourceType.LI_PEOPLE && (
					<>
						<StepInfo
							description={
								<>
									<p>
										Signalist lets you scrape LinkedIn searches two ways: by pasting a Premium
										search URL (e.g. “Sales Manager”) or by setting up filters in-app (title,
										location, language, industry, company size, connection degree). Then choose
										one-off or daily updates and pick which data to export (profiles, emails, phone
										numbers).
									</p>
								</>
							}
							title="Build a lead list from the Linkedin search"
							videoId="pJ6iTtEhDhM"
						/>
						<div className="flex flex-row gap-12">
							<div className="grow relative w-full max-w-[50%]">
								{selected ? (
									<CreateLiPeopleForm
										handleSelect={selected ? setSelected : undefined}
										liPeople={selected as LiPeople}
										list={data as List}
									/>
								) : duplicated ? (
									<CreateLiPeopleForm
										handleDuplicate={setDuplicated}
										handleSelect={setSelected}
										liPeople={duplicated as LiPeople}
										list={data as List}
									/>
								) : (
									<CreateLiPeopleForm list={data as List} />
								)}
							</div>
							<div className="w-1/2">
								<AddedSourceList
									handleDuplicate={(item) => {
										setDuplicated(item);
										setSelected(null);
									}}
									handleSelect={(item) => {
										setSelected(item);
										setDuplicated(null);
									}}
									list={data as List}
								/>
							</div>
						</div>
					</>
				)}

				<CustomEventSourceSection data={data as List} />
			</div>
			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button disabled={amountOfSources === 0} title={t("basics.next")} onClick={submit} />
				</div>
			</div>
			{isRestartPromptOpen && (
				<RestartProcessingModal
					handleClose={skipRestart}
					handleConfirm={confirmRestart}
					isLoading={isRestarting}
					isOpened
				/>
			)}
		</ListBuilderLayout>
	);
};
